import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { GET_PROPERTIES } from '../../../graphql/queries';
import { getCurrentDateString, getFutureDateString } from '../utils/timeUtils';

/**
 * Custom hook for property search functionality
 * @param {boolean} isOpen - Whether the modal is open
 * @returns {Object} Property search state and functions
 */
export default function usePropertySearch(isOpen = false) {
  const [propertySearchTerm, setPropertySearchTerm] = useState('');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showPropertyDropdown, setShowPropertyDropdown] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  
  const propertySearchRef = useRef(null);

  // Property search query
  const { data: propertiesData, loading: propertiesLoading, error: propertiesError } = useQuery(GET_PROPERTIES, {
    variables: {
      search: propertySearchTerm,
      limit: 20,
      page: 1,
      startDate: getCurrentDateString(),
      endDate: getFutureDateString(90)
    },
    skip: !isOpen,
    onError: (error) => {
      console.error('Property search error:', error);
    }
  });

  // Handle clicking outside the property dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (propertySearchRef.current && !propertySearchRef.current.contains(event.target)) {
        setShowPropertyDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Reset search state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setPropertySearchTerm('');
      setSelectedProperty(null);
      setShowPropertyDropdown(false);
      if (searchTimeout) {
        clearTimeout(searchTimeout);
        setSearchTimeout(null);
      }
    }
  }, [isOpen, searchTimeout]);

  /**
   * Handle property search input changes
   * @param {string} newValue - New search value
   */
  const handleSearchChange = (newValue) => {
    setPropertySearchTerm(newValue);
    
    // Clear the previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Clear selected property if search term changes
    if (selectedProperty && newValue !== selectedProperty.name) {
      setSelectedProperty(null);
    }
    
    // Show the dropdown immediately
    setShowPropertyDropdown(true);
    
    // Debounce the actual search to avoid too many API calls
    if (newValue.length > 1) {
      const timeoutId = setTimeout(() => {
        console.log('Executing search for:', newValue);
      }, 300);
      setSearchTimeout(timeoutId);
    }
  };

  /**
   * Handle property selection from dropdown
   * @param {Object} property - Selected property object
   */
  const handlePropertySelect = (property) => {
    setSelectedProperty(property);
    setPropertySearchTerm(property.name);
    setShowPropertyDropdown(false);
  };

  /**
   * Clear selected property
   */
  const clearSelectedProperty = () => {
    setSelectedProperty(null);
    setPropertySearchTerm('');
  };

  /**
   * Handle search input focus
   */
  const handleSearchFocus = () => {
    setShowPropertyDropdown(true);
  };

  // Get processed properties data
  const properties = propertiesData?.propertiesWithReservations?.properties || [];
  
  return {
    // State
    propertySearchTerm,
    selectedProperty,
    showPropertyDropdown,
    properties,
    propertiesLoading,
    propertiesError,
    propertySearchRef,
    
    // Actions
    handleSearchChange,
    handlePropertySelect,
    clearSelectedProperty,
    handleSearchFocus,
    setShowPropertyDropdown
  };
} 